<template>
  <v-dialog v-model="dialog" max-width="720" scrollable>
    <v-card :loading="isLoading">
      <v-card-title class="headline">
        {{ $t("STOCK.INVENTORY.HOLDS_TITLE") }}
      </v-card-title>
      <v-card-subtitle v-if="productLabel" class="pb-0">
        {{ productLabel }}
        <span v-if="warehouseLabel" class="grey--text"> · {{ warehouseLabel }}</span>
        <span v-if="lotNumber" class="grey--text"> · {{ lotNumber }}</span>
      </v-card-subtitle>
      <v-card-text>
        <v-alert v-if="errorMessage" type="error" dense text>{{ errorMessage }}</v-alert>
        <v-simple-table v-else-if="holds.length" dense>
          <thead>
            <tr>
              <th>{{ $t("STOCK.INVENTORY.LOT") }}</th>
              <th class="text-right">{{ $t("STOCK.INVENTORY.BLOCKED") }}</th>
              <th>{{ $t("STOCK.INVENTORY.BLOCK_REASON") }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="hold in holds" :key="hold.id">
              <td>{{ (hold.lot && hold.lot.lot_number) || "-" }}</td>
              <td class="text-right">{{ formatQty(hold.quantity) }}</td>
              <td>{{ hold.reason }}</td>
              <td class="text-right">
                <v-btn
                  v-if="canRelease"
                  x-small
                  text
                  color="primary"
                  :loading="releasingId === hold.id"
                  @click="releaseHold(hold)"
                >
                  {{ $t("STOCK.INVENTORY.RELEASE_HOLD") }}
                </v-btn>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
        <v-alert v-else-if="!isLoading" type="info" dense text>
          {{ $t("STOCK.INVENTORY.NO_HOLDS") }}
        </v-alert>

        <v-divider v-if="releaseTarget" class="my-4" />
        <div v-if="releaseTarget" class="pt-2">
          <div class="subtitle-2 mb-2">
            {{ $t("STOCK.INVENTORY.RELEASE_HOLD") }}:
            {{ formatQty(releaseTarget.quantity) }}
          </div>
          <v-text-field
            v-model.number="releaseQuantity"
            type="number"
            step="0.0001"
            min="0.0001"
            :label="$t('STOCK.INVENTORY.RELEASE_QUANTITY')"
            outlined
            dense
            class="mb-2"
          />
          <v-textarea
            v-model="releaseReason"
            :label="$t('STOCK.INVENTORY.RELEASE_REASON')"
            outlined
            dense
            rows="2"
            auto-grow
            class="mb-2"
          />
          <v-btn
            color="primary"
            small
            :loading="releasingId === releaseTarget.id"
            :disabled="!canConfirmRelease"
            @click="confirmRelease"
          >
            {{ $t("STOCK.INVENTORY.CONFIRM_RELEASE") }}
          </v-btn>
          <v-btn text small class="ml-2" @click="cancelRelease">
            {{ $t("GENERAL.LABELS.CANCEL") }}
          </v-btn>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text color="primary" @click="dialog = false">
          {{ $t("GENERAL.LABELS.CLOSE") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  GET_STOCK_QUALITY_HOLDS,
  RELEASE_STOCK_QUALITY_HOLD,
} from "@/store/stock/stockQualityHolds.module";

export default {
  name: "StockQualityHoldsListDialog",
  props: {
    value: { type: Boolean, default: false },
    warehouseId: { type: Number, default: null },
    warehouseLabel: { type: String, default: "" },
    stockProductId: { type: Number, default: null },
    productLabel: { type: String, default: "" },
    stockLotId: { type: Number, default: null },
    lotNumber: { type: String, default: "" },
  },
  data() {
    return {
      isLoading: false,
      errorMessage: "",
      holds: [],
      releasingId: null,
      releaseTarget: null,
      releaseQuantity: null,
      releaseReason: "",
    };
  },
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    canRelease() {
      return this.hasPermissions("stock-quality-holds-release")
        || this.hasPermissions("stock-quality-holds-create");
    },
    canConfirmRelease() {
      if (!this.releaseTarget) {
        return false;
      }
      const qty = Number(this.releaseQuantity);
      return qty > 0 && qty <= Number(this.releaseTarget.quantity) + 0.0001;
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.releaseTarget = null;
        this.loadHolds();
      }
    },
  },
  methods: {
    formatQty(value) {
      return Number(value || 0).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 4,
      });
    },
    loadHolds() {
      this.isLoading = true;
      this.errorMessage = "";
      this.$store
        .dispatch(GET_STOCK_QUALITY_HOLDS, {
          warehouse_id: this.warehouseId,
          stock_product_id: this.stockProductId,
          stock_lot_id: this.stockLotId || undefined,
          status: "active",
          itemsPerPage: 100,
        })
        .then((data) => {
          this.holds = data.data || data || [];
        })
        .catch((err) => {
          this.errorMessage = err?.message || this.$t("STOCK.INVENTORY.HOLDS_LOAD_ERROR");
          this.holds = [];
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    releaseHold(hold) {
      this.releaseTarget = hold;
      this.releaseQuantity = Number(hold.quantity);
      this.releaseReason = "";
    },
    cancelRelease() {
      this.releaseTarget = null;
      this.releaseQuantity = null;
      this.releaseReason = "";
    },
    confirmRelease() {
      if (!this.canConfirmRelease) {
        return;
      }
      const hold = this.releaseTarget;
      this.releasingId = hold.id;
      this.$store
        .dispatch(RELEASE_STOCK_QUALITY_HOLD, {
          id: hold.id,
          release_quantity: Number(this.releaseQuantity),
          release_reason: this.releaseReason || undefined,
        })
        .then(() => {
          this.cancelRelease();
          this.loadHolds();
          this.$emit("saved");
        })
        .catch((err) => {
          const messages = err?.errors
            ? Object.values(err.errors).flat().join(" ")
            : err?.message;
          this.errorMessage = messages || this.$t("STOCK.INVENTORY.RELEASE_ERROR");
        })
        .finally(() => {
          this.releasingId = null;
        });
    },
  },
};
</script>
